import { makeAutoObservable, runInAction } from "mobx";
import { IHintsMap, ITranslationRecord, ITranslationsService } from "../../services/api/translations-service";
import { rootContainer, TYPE } from "../../services/container/root";
import { getLanguageLabel } from './language-label';
import {IChangeable, ISaveable, WithOld} from "../../stores/types";
import {
    resetTranslationEditSession,
    trackTranslationEditStarted,
    trackTranslationSaveCompleted,
} from "../metrics/translations-mixpanel";
// import translationsMock from './mocks/translations.json';
// import localesMock from './mocks/locales.json';

export interface ITranslationScopeStore extends ISaveable, IChangeable<ITranslationRecord[]> {
    get scope(): string;
    get defaultLocale(): string;
    get localeList(): ILocaleItem[];
    get isLoading(): boolean;
    load(): void;
    getTranslation(key:string, locale:string): string | undefined;
    getDefaultTranslation(key:string): string | undefined;
    getHint(key:string): string | undefined;
    setTranslation(key:string, locale:string, translation:string, type?:string): void;
    copyTranslations(sourceKey: string, targetKey: string): void;
    hasMissingTranslations(locales:string[], keys:string[]): boolean;
    countMissingTranslations(keys: string[]): number;
    enabledLocaleCount(): number;
    discardChanges(): void;
    hasTranslations(keys: string[]): boolean;

    setDeleted(key: string, locale: string): void;
    setDeletedKeys(keys: string[]): void;
    restoreDeleted(key: string, locale: string): void;
    restoreDeletedKeys(keys: string[]): void;
}

export interface ILocaleItem {
    label: string;
    value: string;
    default: boolean;
    isEnabled: boolean;
}

interface ITranslationsMap {
    [locale: string]: {
        [key: string]: ITranslationRecord
    }
}



export default class TranslationScopeStore implements ITranslationScopeStore {
    private _translationsMap: ITranslationsMap = {};
    private _changesMap: ITranslationsMap = {};
    private _hints: IHintsMap = {};

    /** private fields */
    private _scope: string;
    private _defaultLocale: string = '';
    private _localeList: ILocaleItem[] = [];
    private _isLoading: boolean = false;
    private _isSaving: boolean = false;

    /** public getters */
    public get scope() { return this._scope; }
    public get defaultLocale() { return this._defaultLocale }
    public get localeList() { return this._localeList }
    public get isLoading() { return this._isLoading; }
    public isSaving(): boolean {
        return this._isSaving;
    }

    private get translationsService(): ITranslationsService {
		return rootContainer.get<ITranslationsService>(TYPE.TranslationsService);
	}    

    constructor(scope: string) {
        makeAutoObservable(this);
        this._scope = scope;
    }

    private async loadTranslations() {
        this._isLoading = true;
        const transScopeResult = await this.translationsService.getScope(this._scope);
        runInAction(() => {
            this._changesMap = {};
            const { default_locale, locales, translations, hints } = transScopeResult;
            this._hints = hints[0];
            this._defaultLocale = default_locale;
            this._localeList = locales.map(value => ({ 
                value: value.locale,
                label: getLanguageLabel(value.locale),
                default: value.locale === this.defaultLocale,
                isEnabled: value.is_enabled
            }));

            translations.forEach(trans => {
                this._translationsMap[trans.locale] = this._translationsMap[trans.locale] ?? {};
                this._translationsMap[trans.locale][trans.key] = trans as ITranslationRecord;
            });
            this._isLoading = false;
        });
    }

    public load(): void {
        this.loadTranslations();
    }

    public getTranslation(key:string, locale: string): string | undefined {
        if (this._changesMap[locale] && this._changesMap[locale][key]) {
            if (this._changesMap[locale][key]._deteted) {
                /** do not display deleted key */
                return undefined;
            }
            return this._changesMap[locale][key].translation;
        }

        if (this._translationsMap[locale] && this._translationsMap[locale][key]) {
            return this._translationsMap[locale][key].translation;
        }
        return undefined;
    }

    public getDefaultTranslation(key:string): string | undefined {
        return this.getTranslation(key, this.defaultLocale);
    }

    public getHint(key: string): string | undefined {
        const cleanKey = key ? key.replace(/_#.+$/, '') : '';
        return this._hints[cleanKey]?.hint;
    }

    public setTranslation(key:string, locale:string, translation:string, type?:string): void {
        // Capture clean/dirty before mutation; track only on the clean → dirty transition.
        const hadChangesBeforeEdit = this.hasChanges();
        const changeRec = this._findOrCreateChangeRecord(key, locale);
        delete changeRec._deteted;
        changeRec.translation = translation;
        if (type) {
            changeRec.type = type;
        }
        // console.log(JSON.parse(JSON.stringify(changeRec)));
        this._cleanupChange(changeRec);
        if (!hadChangesBeforeEdit && this.hasChanges()) {
            this._trackEditStarted(locale);
        }
    }

    public copyTranslations(sourceKey: string, targetKey: string): void {
        if (sourceKey === targetKey) {
            return;
        }

        this.localeList.forEach(({ value: locale }) => {
            const sourceTranslation = this.getTranslation(sourceKey, locale);
            const targetTranslation = this.getTranslation(targetKey, locale);
            if (sourceTranslation !== undefined && targetTranslation === undefined) {
                this.setTranslation(targetKey, locale, sourceTranslation);
            }
        });
    }

    public setDeleted(key: string, locale: string): void {
        // Capture clean/dirty before mutation; track only on the clean → dirty transition.
        const hadChangesBeforeEdit = this.hasChanges();
        const changeRec = this._findOrCreateChangeRecord(key, locale);
        changeRec._deteted = true;
        this._cleanupChange(changeRec);
        if (!hadChangesBeforeEdit && this.hasChanges()) {
            this._trackEditStarted(locale);
        }
    }

    /** Will delete translations for those keys for all locales */
    public setDeletedKeys(keys: string[]) {
        this.localeList.forEach(locale => {
            keys.forEach(key => {
                this.setDeleted(key, locale.value);
            })
        })
    }

    public restoreDeleted(key: string, locale: string): void {
        const changeRec = this._findChangeRecord(key, locale);
        if (!changeRec) {
            return;
        }
        delete changeRec._deteted;
        this._cleanupChange(changeRec);
    }

    /** Will restore translations for those keys for all locales */
    public restoreDeletedKeys(keys: string[]): void {
        this.localeList.forEach(locale => {
            keys.forEach(key => {
                this.restoreDeleted(key, locale.value);
            })
        })
    }

    /** this method will delete record from _changes if 
     *  - the record is marked as deleted and there is no same record by key in model (doesn't exist on server)
     *  - the record in changes same as one in model by key and translation (eg. no changes) */
    private _cleanupChange(changeRec: ITranslationRecord): void {
        const removeChange = (locale: string, key: string) => {
            /** delete change record */
            delete this._changesMap[locale][key];
            /** delete change locale if empty */
            if (!Object.keys(this._changesMap[locale]).length) {
                delete this._changesMap[locale];
            }
        }

        const { locale, key } = changeRec;
        const modelRec = this._translationsMap[locale]?.[key];

        if (changeRec._deteted) {
            if (!modelRec) {
                /** if no such record in model - remove change */
                removeChange(locale, key);
            }
        }
        /** if same record exists in _model - remove change */
        else if (modelRec && modelRec.translation === changeRec.translation) {
            removeChange(locale, key);
        }
        
        clearTimeout(this._logInterval);
        this._logInterval = setTimeout(() => {
            // console.log('trans changes', JSON.parse(JSON.stringify(this._changesMap)));
        }, 0);
    }
    private _logInterval: any;

    private commitChanges() {
        const changesList = this._getChanges();

        changesList.forEach(change => {
            if (change._deteted) {
                const modelRec = this._translationsMap[change.locale]?.[change.key];
                if (modelRec) {
                    delete this._translationsMap[change.locale][change.key];
                }
                else { /** should never happen */
                    console.error('attempt to delete from server not existing translation key');
                }
                return;
            }

            if (!this._translationsMap[change.locale]) {
                this._translationsMap[change.locale] = {};
            }
            this._translationsMap[change.locale][change.key] = { ...change };            
        });

        this._changesMap = {};
    }

    private _getChanges(): ITranslationRecord[] {
        const results: ITranslationRecord[] = [];
        Object.keys(this._changesMap).forEach(locale => {
            Object.keys(this._changesMap[locale]).forEach(key => {
                results.push({ ...this._changesMap[locale][key] });
            })
        });
        return results;
    }
    
    /** saves all edited translation records */
    public async save(): Promise<void> {
        this._isSaving = true;
        const changesList = this._getChanges();
        const deletedRecordList = changesList.filter(c => c._deteted);
        const changedRecordList: WithOld<ITranslationRecord[]> = changesList.filter(c => !c._deteted);
        // Get the `old` value to save to the Audit log
        const __old = changesList.map(({ key, locale }) => this._translationsMap[locale]?.[key]).filter(Boolean);
        const promises = [];
        const changedKeys = Array.from(new Set(changesList.map(c => c.key)));
        const localesChangedCount = Object.keys(this._changesMap).length;
        const primaryLocale = changesList[0]?.locale ?? this._defaultLocale;
        const missingBefore = this.countMissingTranslations(changedKeys);
        const startedAt = Date.now();

        if (changedRecordList.length) {
            promises.push(this.translationsService.saveTranslations({ payload: changedRecordList, __old, }));
        }

        if (deletedRecordList.length) {
            promises.push(this.translationsService.deleteTranslations({ payload: deletedRecordList, __old, }));
        }

        if (promises.length) {
            try {
                this._isSaving = true;
                await Promise.all(promises);
                runInAction(() => {
                    this._isSaving = false;
                    /** after successful save api call, commit changed data on client */
                    this.commitChanges();
                    console.log('commit changes called');
                    trackTranslationSaveCompleted({
                        scope: this._scope,
                        locale: primaryLocale,
                        is_default_locale: primaryLocale === this._defaultLocale,
                        result: 'success',
                        fields_changed_count: changesList.length,
                        locales_changed_count: localesChangedCount,
                        missing_before: missingBefore,
                        missing_after: this.countMissingTranslations(changedKeys),
                        duration_ms: Date.now() - startedAt,
                        enabled_locale_count: this.enabledLocaleCount(),
                    });
                });
            }
            catch (err) {
                runInAction(() => {
                    this._isSaving = false;
                    trackTranslationSaveCompleted({
                        scope: this._scope,
                        locale: primaryLocale,
                        is_default_locale: primaryLocale === this._defaultLocale,
                        result: 'error',
                        fields_changed_count: changesList.length,
                        locales_changed_count: localesChangedCount,
                        missing_before: missingBefore,
                        missing_after: missingBefore,
                        duration_ms: Date.now() - startedAt,
                        enabled_locale_count: this.enabledLocaleCount(),
                    });
                });    
            }
        }
    }

    private _findChangeRecord(key: string, locale: string): ITranslationRecord|undefined {
        return this._changesMap[locale]?.[key];
    }
    
    private _findOrCreateChangeRecord(key: string, locale:string): ITranslationRecord {
        const modelRec = this._translationsMap[locale]?.[key];
        
        if (!this._changesMap[locale]) {
            this._changesMap[locale] = {};
        }
        if (!this._changesMap[locale][key]) {
            if (modelRec) {
                this._changesMap[locale][key] = { ...modelRec };
            }
            else {
                this._changesMap[locale][key] = {
                    key,
                    locale,
                    scope: this._scope,
                    translation: '',
                };
            }
        }
        return this._changesMap[locale][key];
    }

    public hasTranslations(keyList: string[]): boolean {
        const hasTranslationsForKeys = !!this.localeList.find(localeRec => {
            return keyList.find(key => !!this.getTranslation(key, localeRec.value));
        });

        return hasTranslationsForKeys;
    }

    public hasChanges(keyList?: string[]): boolean {
        if (keyList) {
            const hasChangesInKeys = !!this.localeList.find(localeRec => {
                return keyList.find(key => !!this._findChangeRecord(key, localeRec.value));
            });

            return hasChangesInKeys;
        }
        
        return !!Object.keys(this._changesMap).length;
    }

    public discardChanges(): void {
        this._changesMap = {};
        resetTranslationEditSession(this._scope);
    }

    public hasMissingTranslations(locales: string[], keys: string[]): boolean {
        return !!locales.find(localeRec => {
            return keys.find(key => !!!this.getTranslation(key, localeRec));
        });
    }

    public countMissingTranslations(keys: string[]): number {
        if (!keys.length) {
            return 0;
        }
        let count = 0;
        this._localeList
            .filter(locale => locale.isEnabled && locale.value !== this._defaultLocale)
            .forEach(locale => {
                keys.forEach(key => {
                    if (!this.getTranslation(key, locale.value)) {
                        count += 1;
                    }
                });
            });
        return count;
    }

    public enabledLocaleCount(): number {
        return this._localeList.filter(locale => locale.isEnabled).length;
    }

    private _trackEditStarted(locale: string): void {
        trackTranslationEditStarted({
            scope: this._scope,
            locale,
            is_default_locale: locale === this._defaultLocale,
        });
    }

    /** stub to implement IChangeable */
    public getUpdatedModel(): ITranslationRecord[] {
        return [];
    }
}

